/**
 * The host's screens for a table that players sign in to on their own phones (see src/ui/tableClient.ts):
 * opening the table (the host signs in first, and agrees to the Terms), then the lobby — the link/QR, who has signed in,
 * who sits where (typed in order, or left for the dice like any game) and Start.
 *
 * The lobby is drawn ONCE and then updated in place as phones sign in, so nothing the host has already typed or chosen
 * (the table ID, the seating) is lost every few seconds.
 */

import { renderBrandHeader } from '../branding';
import { esc } from '../helpers';
import type { LeaderboardPeriodInfo } from '../leaderboardClient';
import { TABLE_SIZE, type TableMember } from '../tableClient';
import { renderGuestLinkQr } from './shareGame';
import { leaderboardTermsHtml } from './activation';
import { describePeriodForPlayers } from './leaderboardSetup';
import type { SeatingChoice } from './setup';

const SEAT_LABELS = ['East (deals first)', 'South', 'West', 'North'];

export interface TableOpenActions {
  open(params: { nickname: string; pin: string }): void;
  back(): void;
}

export function renderTableOpen(
  root: HTMLElement,
  period: LeaderboardPeriodInfo,
  defaults: { nickname: string },
  error: string | undefined,
  actions: TableOpenActions,
): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Players sign in on their own phones</h1>
      <p class="muted">
        Open a table, then show its QR code to the other players. Each of them signs in on their own phone with their own
        nickname and PIN, so nobody has to type a PIN on someone else's screen. Start by signing in yourself.
      </p>
      <div class="lb-note">${esc(describePeriodForPlayers(period))}</div>
      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}

      <label for="tblNick">Your nickname</label>
      <input type="text" id="tblNick" autocomplete="off" maxlength="20" value="${esc(defaults.nickname)}" />
      <label for="tblPin">Your PIN</label>
      <input type="password" id="tblPin" inputmode="numeric" maxlength="4" autocomplete="off" />
      <p class="muted" style="margin:0.5rem 0 0">
        Staff just unlocked your account? Your PIN is now the standard temporary one — you will be asked to choose your own straight away.
      </p>

      <details style="margin-top:1rem"><summary>Leaderboard terms — by opening a table you agree to them</summary>${leaderboardTermsHtml()}</details>

      <button id="tblOpenBtn" style="margin-top:1rem;width:100%">Open table</button>
      <button id="tblBackBtn" class="ghost" type="button" style="width:100%;margin-top:0.5rem">← Back</button>
    </div>
  `;
  const btn = root.querySelector<HTMLButtonElement>('#tblOpenBtn')!;
  btn.addEventListener('click', () => {
    btn.disabled = true;
    actions.open({
      nickname: root.querySelector<HTMLInputElement>('#tblNick')!.value.trim(),
      pin: root.querySelector<HTMLInputElement>('#tblPin')!.value.trim(),
    });
  });
  root.querySelector<HTMLButtonElement>('#tblBackBtn')!.addEventListener('click', () => actions.back());
}

export interface TableLobbyInfo {
  period: LeaderboardPeriodInfo;
  link: string;
  /** Whether the staff must approve the table: the button then SENDS it instead of starting the game. */
  approvalRequired: boolean;
  defaults: { tableId: string; seating: SeatingChoice };
}

export interface TableLobbyActions {
  start(params: { tableId: string; seating: SeatingChoice; players: string[] }): void;
  remove(nickname: string): void;
  cancel(): void;
}

/** What the lobby lets the caller do after it is drawn: keep it current, and show a problem without redrawing. */
export interface TableLobbyController {
  update(members: TableMember[]): void;
  showError(message: string): void;
  /** Lets the host press the button again after a refusal. */
  ready(): void;
  /** The relay says whether the staff must approve tables; the button follows. */
  setApprovalRequired(required: boolean): void;
}

export function renderTableLobby(
  root: HTMLElement,
  info: TableLobbyInfo,
  initialMembers: TableMember[],
  actions: TableLobbyActions,
): TableLobbyController {
  const seating0: SeatingChoice = info.defaults.seating === 'dice' ? 'dice' : 'entered';
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card" id="tableLobby">
      <h1>Your table is open</h1>
      <p class="muted">
        Show this to the other players. They scan it with their phone's camera, sign in with their own nickname and PIN
        (or create a User ID), and appear below. The table closes by itself after 2 hours.
      </p>
      <div class="lb-note">${esc(describePeriodForPlayers(info.period))}</div>

      <div style="display:flex;justify-content:center;margin:1rem 0">${renderGuestLinkQr(info.link)}</div>
      <label for="tblLink">Link</label>
      <input type="text" id="tblLink" readonly value="${esc(info.link)}" style="width:100%" />
      <div class="row" style="margin-top:0.5rem"><button type="button" id="tblCopyBtn" class="secondary">Copy link</button></div>
      <p id="tblCopied" class="muted" style="display:none;margin-top:0.3rem">Copied.</p>

      <h2 style="margin-top:1rem">Signed in</h2>
      <div id="tblMembers"></div>
      <p class="muted" id="tblWaiting"></p>

      <label for="tblTableId">Table name</label>
      <input type="text" id="tblTableId" placeholder="e.g. Table 3" maxlength="40" value="${esc(info.defaults.tableId)}" />

      <label>Who sits where?</label>
      <div class="seating-choice" role="radiogroup" aria-label="How the seating is decided">
        <label class="seating-option">
          <input type="radio" name="tblSeating" value="entered" ${seating0 === 'entered' ? 'checked' : ''} />
          <span><strong>I'll set the seating now</strong><small>Choose who sits East, South, West and North.</small></span>
        </label>
        <label class="seating-option">
          <input type="radio" name="tblSeating" value="dice" ${seating0 === 'dice' ? 'checked' : ''} />
          <span><strong>Decide with the dice</strong><small>Once everyone has rolled the dice, you set who sits where.</small></span>
        </label>
      </div>
      <div id="tblSeatPickers" class="stack"></div>

      <div id="tblError"></div>
      <p class="muted" id="tblApprovalNote" style="margin:0.75rem 0 0"></p>
      <button id="tblStartBtn" style="margin-top:0.5rem;width:100%" disabled>Start Game</button>
      <button id="tblCancelBtn" class="ghost" type="button" style="width:100%;margin-top:0.5rem">Cancel this table</button>
    </div>
  `;

  let members: TableMember[] = [];
  const membersBox = root.querySelector<HTMLElement>('#tblMembers')!;
  const waiting = root.querySelector<HTMLElement>('#tblWaiting')!;
  const pickers = root.querySelector<HTMLElement>('#tblSeatPickers')!;
  const startBtn = root.querySelector<HTMLButtonElement>('#tblStartBtn')!;
  const errorBox = root.querySelector<HTMLElement>('#tblError')!;

  const chosenSeating = (): SeatingChoice =>
    root.querySelector<HTMLInputElement>('input[name="tblSeating"]:checked')?.value === 'dice' ? 'dice' : 'entered';
  const pickerValues = (): string[] => [0, 1, 2, 3].map((i) => root.querySelector<HTMLSelectElement>(`#tblSeat-${i}`)?.value ?? '');

  /** The seating to send: the four picks in wind order, or the join order when the dice decide. */
  const seatingOrder = (): string[] => (chosenSeating() === 'dice' ? members.map((m) => m.nickname) : pickerValues());

  const problem = (): string | undefined => {
    if (members.length < TABLE_SIZE) {
      const missing = TABLE_SIZE - members.length;
      return `Waiting for ${missing} more ${missing === 1 ? 'player' : 'players'} to sign in.`;
    }
    if (chosenSeating() === 'entered') {
      const picks = pickerValues();
      if (picks.some((p) => !p)) return 'Choose a player for every seat.';
      if (new Set(picks).size !== TABLE_SIZE) return 'Each seat needs a different player.';
    }
    return undefined;
  };

  let approvalRequired = info.approvalRequired;
  const startLabel = () => (approvalRequired ? 'Send to staff for approval' : 'Start Game');
  const refreshStart = () => {
    const why = problem();
    startBtn.disabled = why !== undefined;
    startBtn.textContent = startLabel();
    waiting.textContent = why ?? 'Everyone is here.';
    root.querySelector<HTMLElement>('#tblApprovalNote')!.textContent = approvalRequired
      ? 'The venue staff approve every table before it plays. You and the players wait on this screen until they do.'
      : '';
  };

  /** Rebuilds the four seat pickers, keeping each pick that is still valid and filling the rest with whoever is unplaced. */
  const buildPickers = () => {
    const keep = pickerValues();
    const names = members.map((m) => m.nickname);
    const next = keep.map((v) => (names.includes(v) ? v : ''));
    const seen = new Set<string>();
    for (let i = 0; i < next.length; i++) {
      if (next[i] && seen.has(next[i]!)) next[i] = '';
      if (next[i]) seen.add(next[i]!);
    }
    for (let i = 0; i < next.length; i++) {
      if (next[i]) continue;
      const free = names.find((n) => !seen.has(n));
      if (free) {
        next[i] = free;
        seen.add(free);
      }
    }
    pickers.style.display = chosenSeating() === 'entered' ? '' : 'none';
    pickers.innerHTML = [0, 1, 2, 3]
      .map(
        (i) => `
        <div class="row" style="gap:0.5rem;align-items:center">
          <span style="flex:1">${esc(SEAT_LABELS[i]!)}</span>
          <select id="tblSeat-${i}" style="flex:2">
            <option value="">— choose —</option>
            ${names.map((n) => `<option value="${esc(n)}" ${next[i] === n ? 'selected' : ''}>${esc(n)}</option>`).join('')}
          </select>
        </div>`,
      )
      .join('');
    for (let i = 0; i < 4; i++) root.querySelector<HTMLSelectElement>(`#tblSeat-${i}`)!.addEventListener('change', refreshStart);
  };

  const drawMembers = () => {
    membersBox.innerHTML = members.length
      ? `<ul class="stack" style="list-style:none;padding:0;margin:0">${members
          .map(
            (m) => `
          <li class="row" style="gap:0.5rem;align-items:center">
            <span style="flex:1">${esc(m.nickname)}${m.isHost ? ' <span class="muted">(you)</span>' : ''}</span>
            ${m.isHost ? '' : `<button type="button" class="ghost tbl-remove" data-nick="${esc(m.nickname)}">Remove</button>`}
          </li>`,
          )
          .join('')}</ul>`
      : '<p class="muted">Nobody yet.</p>';
    for (const btn of membersBox.querySelectorAll<HTMLButtonElement>('.tbl-remove')) {
      btn.addEventListener('click', () => {
        btn.disabled = true;
        actions.remove(btn.dataset.nick!);
      });
    }
  };

  const sameMembers = (a: TableMember[], b: TableMember[]) => a.length === b.length && a.every((m, i) => m.nickname === b[i]!.nickname);

  const controller: TableLobbyController = {
    update(next) {
      if (sameMembers(members, next)) return; // nothing changed: leave the screen alone
      members = next;
      drawMembers();
      buildPickers();
      refreshStart();
    },
    showError(message) {
      errorBox.innerHTML = message ? `<div class="error-box">${esc(message)}</div>` : '';
    },
    ready() {
      refreshStart();
    },
    setApprovalRequired(required) {
      if (required === approvalRequired) return;
      approvalRequired = required;
      refreshStart();
    },
  };

  for (const radio of root.querySelectorAll<HTMLInputElement>('input[name="tblSeating"]')) {
    radio.addEventListener('change', () => {
      buildPickers();
      refreshStart();
    });
  }

  const link = root.querySelector<HTMLInputElement>('#tblLink')!;
  root.querySelector<HTMLButtonElement>('#tblCopyBtn')!.addEventListener('click', () => {
    link.select();
    void navigator.clipboard
      ?.writeText(info.link)
      .then(() => {
        root.querySelector<HTMLElement>('#tblCopied')!.style.display = '';
      })
      .catch(() => {
        // The field is selected for a manual copy, so this is not a dead end.
      });
  });

  startBtn.addEventListener('click', () => {
    if (problem()) return;
    startBtn.disabled = true;
    startBtn.textContent = approvalRequired ? 'Sending…' : 'Starting…';
    controller.showError('');
    actions.start({
      tableId: root.querySelector<HTMLInputElement>('#tblTableId')!.value.trim(),
      seating: chosenSeating(),
      players: seatingOrder(),
    });
  });
  root.querySelector<HTMLButtonElement>('#tblCancelBtn')!.addEventListener('click', () => actions.cancel());

  controller.update(initialMembers);
  // First draw with no members yet still needs its empty state.
  if (initialMembers.length === 0) {
    drawMembers();
    buildPickers();
    refreshStart();
  }
  return controller;
}
